import { BODY_PARAMS_KEY, BODY_PARAMS_KEY_ALLOWED_METHODS } from "diagnostics-lib/constants";
import { ApiError } from "./error";
import { MTS_ENV } from "../lib/setupEnv";
// TODO: FIX FRONT IS DEPENDENT ON API TYPES

import type { EndpointTree } from "../../../service-diagnostics-core/src/Endpoint";
import { CLIENT_ERROR, CUSTOM_HEADER_PREFIX } from "diagnostics-lib/constants";
import { getAuthentifiedUser } from "../lib/user";

// what you send to the server (routeParams, query, body, headers), along with the method and path
export type Input<M extends keyof EndpointTree, P extends keyof EndpointTree[M]> = Endpoint<
  EndpointTree[M][P]
>["input"];

// options for the call function behavior
type Options<M extends keyof EndpointTree, P extends keyof EndpointTree[M]> = {
  signal?: AbortSignal;
  jsonStreamCallback?: (output: Output<M, P>["body"], done: boolean) => void;
};

// what you get from the server (status, body, headers) and the streaming in progress flag
export type Output<M extends keyof EndpointTree, P extends keyof EndpointTree[M]> = Exclude<
  Endpoint<EndpointTree[M][P]>["output"],
  {
    status:
      | (typeof CLIENT_ERROR)[keyof typeof CLIENT_ERROR]
      | 301
      | 303
      | 304
      | 500
      | 501
      | 502
      | 503;
  }
> & { streaming: boolean };

async function call<M extends keyof EndpointTree, P extends keyof EndpointTree[M]>(
  method: M,
  path: P,
  { routeParams, body: requestBody, headers: headerParams, query }: Input<M, P>,
  { signal, jsonStreamCallback }: Options<M, P> = {},
): Promise<Output<M, P>> {
  const user = getAuthentifiedUser();
  const url = buildURL(method, path as string, routeParams ?? null, query ?? null, requestBody);
  const isJSONBody = Boolean(requestBody) && !(requestBody instanceof FormData);
  const isFormDataBody = Boolean(requestBody) && requestBody instanceof FormData;

  const requestContentType = isFormDataBody
    ? undefined // https://stackoverflow.com/a/68781155
    : isJSONBody
      ? "application/json; charset=utf-8"
      : "text/plain; charset=utf-8";

  const requestHeaders: Record<string, string> = {
    ...(user === null ? undefined : { Authorization: `Bearer ${user.access_token}` }),
    ...(requestContentType && { "Content-type": requestContentType }),
    ...headerParams,
  };

  let debugData: { [k: string]: unknown } = {
    timestamp: Date.now(),
    method,
    path,
    HOST,
    url,
    requestContentType,
    routeParams,
    requestBody,
    requestHeaders,
    signal,
    authentication: user,
  };

  const request = new window.Request(url.href, {
    method,
    headers: requestHeaders,
    body:
      requestBody === undefined || BODY_PARAMS_KEY_ALLOWED_METHODS.includes(method)
        ? undefined
        : isFormDataBody
          ? requestBody
          : JSON.stringify(requestBody),
  });

  let response;
  try {
    response = await fetch(request, { signal });
  } catch (cause) {
    throw new ApiError("network", {
      message: `[${method} ${path as string}] ${errorMessage(cause)}`,
      cause,
      debugData,
    });
  }

  const responseStatus = response.status;

  debugData = {
    ...debugData,
    responseStatus,
  };

  const responseContentType = response.headers.get("content-type");

  debugData = {
    ...debugData,
    responseContentType,
  };

  const responseHeaders: Record<string, unknown> = {};
  for (const [key, value] of response.headers.entries()) {
    const lowerCaseKey = key.toLowerCase();
    if (lowerCaseKey.startsWith(CUSTOM_HEADER_PREFIX)) {
      responseHeaders[lowerCaseKey.replace(CUSTOM_HEADER_PREFIX, "")] = JSON.parse(value);
    }
  }

  debugData = {
    ...debugData,
    responseHeaders,
  };

  const responseType =
    responseContentType === null
      ? "NO-CONTENT"
      : responseContentType.includes("text")
        ? "TEXT"
        : responseContentType.includes("stream+json")
          ? "JSON-STREAM"
          : responseContentType.includes("json")
            ? "JSON"
            : responseContentType.includes("octet-stream")
              ? "BLOB"
              : null;

  debugData = {
    ...debugData,
    responseType,
  };

  if (responseType === null) {
    throw new ApiError("contract_failure", {
      message: `[${method} ${path as string}] invalid content-type: ${responseContentType}`,
      debugData,
    });
  }

  let responseBody;
  let streaming = false;

  try {
    switch (responseType) {
      case "JSON-STREAM": {
        const body = response.body;
        if (!body) throw new Error("no reader");
        const delimiter = responseHeaders["json-stream-delimiter"];
        if (typeof delimiter !== "string" || !delimiter)
          throw new ApiError("contract_failure", {
            message: `[${method} ${path as string}] missing "json-stream-delimiter" in headers for json-stream response`,
            debugData,
          });

        const frontEndStream = frontEndStreamingSupported
          ? body.pipeThrough(new TextDecoderStream()).getReader()
          : false;

        if (!frontEndStream) {
          const content = await response.text();
          responseBody = content.split(delimiter).filter(Boolean).map(parse);
          break;
        }

        if (!jsonStreamCallback) {
          console.debug(
            `[${method} ${String(path)}] JSON stream received, but no jsonStreamCallback not provided`,
            debugData,
          );
          let str = "";
          while (true) {
            const { done, value } = await frontEndStream.read();
            if (done) break;
            str += value;
          }

          // we are not streaming, we return a single responseBody
          responseBody = str.split(delimiter).filter(Boolean).map(parse);

          break;
        }

        // we are streaming, this will be read in useQuery through header
        streaming = true;

        // we return a responseBody with the first valid chunk
        responseBody = await new Promise<Output<M, P>["body"]>(resolve => {
          let results: unknown[] = [];

          // we use a setTimeout to avoid blocking the main thread and allow React to render with the first chunk (otherwise it would wait for the whole stream)
          window.setTimeout(async () => {
            let remainder = ""; // array elements may be split in the stream, we need to store the remainders
            let resolved = false;

            // we read the stream to the end and call jsonStreamCallback at each valid chunk
            while (true) {
              const { done, value } = await frontEndStream.read();

              // nothing in the stream
              if (done) {
                if (remainder) results.push(parse(remainder));
                if (!resolved) resolve(results);
                jsonStreamCallback(results, true);
                break;
              } else {
                const fullValue = remainder + value;
                const splat = fullValue.split(delimiter);
                if (splat.length === 1) {
                  // edge case, no separator in the chunk, and remainder + splat[0] may not a real value yet
                  remainder = fullValue;
                } else {
                  const firstValue = splat[0]!; // not empty (length > 0)
                  const middleValues = splat.slice(1, -1); // may be empty (length <= 2)
                  const lastValue = splat[splat.length - 1]!; // not empty (length > 0), and different from firstValue (length > 1)
                  const validValues = [firstValue, ...middleValues]; // not empty (firstValue)
                  remainder = lastValue;
                  results = results.concat(validValues.map(parse));

                  if (resolved) {
                    // stream a new chunk
                    jsonStreamCallback(results, false);
                  } else {
                    // return first valid results and un-suspend useQuery
                    resolved = true;
                    resolve(results);
                  }
                }
              }
            }
          });
        });
        break;
      }
      case "JSON":
        responseBody = await response.json();
        break;

      case "TEXT":
        responseBody = await response.text();
        break;

      case "BLOB":
        responseBody = await response.blob();
        break;

      case "NO-CONTENT":
        responseBody = undefined;
        break;

      default:
        throw new Error(responseType satisfies never);
    }
  } catch (cause) {
    // eslint-disable-next-line no-console
    console.error(cause);
    throw new ApiError("contract_failure", {
      message: `[${method} ${String(path)}] cannot parse response body, ${errorMessage(cause)}`,
      cause,
      debugData,
    });
  }

  debugData = {
    ...debugData,
    responseBody,
  };

  for (const statusKey of Object.keys(CLIENT_ERROR) as (keyof typeof CLIENT_ERROR)[]) {
    if (CLIENT_ERROR[statusKey] === responseStatus)
      throw new ApiError(statusKey, {
        message: `[${method} ${String(path)}] ${responseBody}`,
        debugData,
      });
  }

  if (responseStatus === 503 || responseStatus === 502)
    throw new ApiError("tmp_backend_error", {
      message: `[${method} ${String(path)}] ${responseBody}`,
      debugData,
    });

  // reject all 5XX
  if (String(responseStatus).length === 3 && String(responseStatus).startsWith("5"))
    throw new ApiError("backend_error", {
      message: `[${method} ${String(path)}] ${responseBody}`,
      debugData,
    });

  return {
    body: responseBody,
    status: responseStatus,
    headers: responseHeaders,
    streaming,
  } as Output<M, P>; // hopefully
}

export default call;

const HOST = MTS_ENV.COREV2_URL;

function castValue(value: unknown): string {
  return typeof value === "object" && value instanceof Date ? value.toJSON() : String(value);
}

function buildPath(path: string, routeParams: Record<string, unknown>) {
  const parts: string[] = [];
  for (const pathPart of path.split("/")) {
    if (!pathPart.startsWith(":")) {
      parts.push(pathPart);
      continue;
    }

    const parenthesisIndex = pathPart.indexOf("(");
    const name = pathPart.slice(1, parenthesisIndex > 0 ? parenthesisIndex : undefined);

    // No `:` in URL
    if (!Object.hasOwn(routeParams, name)) throw new Error("Missing route parameter");

    const value = routeParams[name];
    if (value === undefined) throw new Error('route param with value "undefined" not supported');
    if (value === null) throw new Error('route param with value "null" not supported');
    if ((typeof value === "object" && !(value instanceof Date)) || Array.isArray(value))
      throw new Error('route param with value "object" or "array" not supported');

    parts.push(castValue(value));
  }

  return parts.join("/");
}

export function buildURL(
  method: string,
  path: string,
  routeParams: Record<string, unknown> | null,
  query: Record<string, unknown> | null,
  body: unknown,
): URL {
  const url = new URL(HOST + (routeParams ? buildPath(path, routeParams) : path));

  if (query)
    for (const [key, value] of Object.entries(query).sort((a, b) => a[0].localeCompare(b[0]))) {
      if (value === null) throw new Error('query param with value "null" not supported');
      if ((typeof value === "object" && !(value instanceof Date)) || Array.isArray(value))
        throw new Error('query param with value "object" or "array" not supported');

      if (value !== undefined) url.searchParams.append(key, castValue(value));
    }

  if (body !== undefined && BODY_PARAMS_KEY_ALLOWED_METHODS.includes(method))
    url.searchParams.append(BODY_PARAMS_KEY, JSON.stringify(body));

  return url;
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  return JSON.stringify(error) || "empty error";
}

function parse(input: string, index?: number): unknown {
  try {
    return JSON.parse(input);
  } catch (e: unknown) {
    const error = e instanceof Error ? e : new Error(String(e));
    // eslint-disable-next-line no-console
    console.error("JSON parse failed:", { index, message: error.message, input });
    throw error;
  }
}

const frontEndStreamingSupported =
  "TextDecoderStream" in window &&
  typeof window.TextDecoderStream === "function" &&
  process.env["NODE_ENV"] !== "test";
if (!frontEndStreamingSupported && process.env["NODE_ENV"] !== "test")
  console.debug("streaming not supported");

interface GenericEndpoint {
  input: {
    routeParams?: Record<string, unknown>;
    query?: Record<string, unknown>;
    headers?: Record<string, unknown>;
    body?: unknown;
  };
  output: {
    status: number;
    body: unknown;
    headers: Record<string, unknown> | undefined;
  };
}
type Endpoint<E> = E extends GenericEndpoint ? E : GenericEndpoint;
